import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Wallet, TrendingUp, Plus, Pencil, Trash2, MoreHorizontal, Loader2, DollarSign,
  Landmark, Banknote, BarChart3, Car, Gem, Home, Bitcoin, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
const fmtCurrencyFull = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

const ASSET_TYPES = [
  { value: 'bank_account', label: 'Bank Account', icon: Landmark },
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'stock', label: 'Stocks', icon: BarChart3 },
  { value: 'crypto', label: 'Crypto', icon: Bitcoin },
  { value: 'real_estate', label: 'Real Estate', icon: Home },
  { value: 'vehicle', label: 'Vehicle', icon: Car },
  { value: 'valuable', label: 'Valuable', icon: Gem },
  { value: 'metal', label: 'Precious Metal', icon: Gem },
  { value: 'other', label: 'Other', icon: Wallet },
];

const TYPE_COLORS = {
  bank_account: '#3B82F6', cash: '#3B82F6', stock: '#8B5CF6', crypto: '#F59E0B',
  real_estate: '#EF4444', vehicle: '#6366F1', valuable: '#EC4899', metal: '#D97706', other: '#6B7280'
};

function AssetDialog({ open, onOpenChange, asset, companyId, onSave, defaultType }) {
  const [form, setForm] = useState({ name: '', asset_type: 'bank_account', value: '', institution: '', account_number: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (asset) {
        setForm({ name: asset.name || '', asset_type: asset.asset_type || 'bank_account', value: String(asset.value || ''), institution: asset.institution || '', account_number: asset.account_number || '', notes: asset.notes || '' });
      } else {
        setForm({ name: '', asset_type: defaultType || 'bank_account', value: '', institution: '', account_number: '', notes: '' });
      }
    }
  }, [asset, open, defaultType]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) || 0, company_id: companyId };
      if (asset) {
        await axios.put(`${API}/api/assets/${asset.id}`, payload, { withCredentials: true });
        toast.success('Asset updated');
      } else {
        await axios.post(`${API}/api/assets`, payload, { withCredentials: true });
        toast.success('Asset added');
      }
      onSave(); onOpenChange(false);
    } catch { toast.error('Failed to save'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{asset ? 'Edit Asset' : 'Add Asset'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Chase Checking" data-testid="asset-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select value={form.asset_type} onValueChange={v => setForm(p => ({...p, asset_type: v}))}>
                <SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input type="number" step="0.01" value={form.value} onChange={e => setForm(p => ({...p, value: e.target.value}))} placeholder="0.00" data-testid="asset-value-input" />
            </div>
          </div>
          <div>
            <Label>Institution</Label>
            <Input value={form.institution} onChange={e => setForm(p => ({...p, institution: e.target.value}))} placeholder="e.g., JPMorgan Chase" data-testid="asset-institution-input" />
          </div>
          <div>
            <Label>Account # (last 4)</Label>
            <Input value={form.account_number} onChange={e => setForm(p => ({...p, account_number: e.target.value}))} placeholder="****1234" data-testid="asset-account-input" />
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Optional" data-testid="asset-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="save-asset-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : asset ? 'Update' : 'Add Asset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PersonalDashboardPage() {
  const { currentCompany } = useCompany();
  const { typeSlug } = useParams();
  const SLUG_TO_TYPE = { vehicles: 'vehicle', metals: 'metal', financial: 'bank_account', investments: 'stock' };
  const SLUG_TO_EXTRA = { financial: ['bank_account', 'cash'], investments: ['stock', 'crypto'] };
  const routeFilter = typeSlug ? (SLUG_TO_EXTRA[typeSlug] || [SLUG_TO_TYPE[typeSlug]]) : null;
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchData = useCallback(async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const [assetsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/assets`, { params: { company_id: currentCompany.id }, withCredentials: true }),
        axios.get(`${API}/api/assets/summary`, { params: { company_id: currentCompany.id }, withCredentials: true })
      ]);
      setAssets(assetsRes.data);
      setSummary(summaryRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentCompany]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/assets/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Asset deleted');
      setDeleteTarget(null); fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const allFiltered = routeFilter ? assets.filter(a => routeFilter.includes(a.asset_type)) : assets;
  const filtered = typeFilter === 'all' ? allFiltered : allFiltered.filter(a => a.asset_type === typeFilter);
  const uniqueTypes = [...new Set(allFiltered.map(a => a.asset_type))];

  const SLUG_TITLES = { vehicles: 'Vehicles', metals: 'Precious Metals', financial: 'Financial Accounts', investments: 'Private Investments' };
  const pageTitle = typeSlug ? SLUG_TITLES[typeSlug] || 'Assets' : 'Assets & Net Worth';
  const defaultAssetType = typeSlug ? (SLUG_TO_TYPE[typeSlug] || 'other') : 'bank_account';

  if (!currentCompany) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;

  const pieData = summary?.by_type?.filter(t => t.value > 0) || [];

  return (
    <div className="space-y-6" data-testid="personal-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{typeSlug ? `Manage your ${pageTitle.toLowerCase()}` : 'Track everything you own'}</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-asset-btn">
          <Plus className="w-4 h-4 mr-2" />Add {typeSlug ? SLUG_TITLES[typeSlug]?.replace(/s$/, '') || 'Asset' : 'Asset'}
        </Button>
      </div>

      {/* Net Worth Card - only on summary page */}
      {!typeSlug && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-200 p-6">
          <p className="text-gray-300 text-sm">Total Net Worth</p>
          <p className="text-4xl font-bold text-white mt-1" data-testid="net-worth-value">{fmtCurrency(summary?.total_net_worth)}</p>
          <div className="flex items-center gap-6 mt-3 text-sm">
            <span className="text-blue-400">Assets: {fmtCurrency(summary?.total_assets)}</span>
            <span className="text-red-400">Liabilities: {fmtCurrency(summary?.total_liabilities)}</span>
          </div>
        </div>
      )}

      {/* Charts Row - only on summary */}
      {!typeSlug && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Allocation Pie */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Asset Allocation</h3>
          {pieData.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="w-40 h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="type" cx="50%" cy="50%" innerRadius={35} outerRadius={65} strokeWidth={2} stroke="#FFFFFF">
                      {pieData.map((entry, i) => <Cell key={i} fill={TYPE_COLORS[entry.type] || '#6B7280'} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#FFFFFF' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-2">
                {pieData.map((t, i) => {
                  const typeInfo = ASSET_TYPES.find(at => at.value === t.type);
                  return (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TYPE_COLORS[t.type] || '#6B7280' }} />
                        <span className="text-gray-500">{typeInfo?.label || t.type}</span>
                      </div>
                      <span className="text-gray-700 font-medium">{t.percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600"><Wallet className="w-8 h-8 mx-auto mb-2" /><p>No assets yet</p></div>
          )}
        </div>

        {/* Net Worth History */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Net Worth History</h3>
          {summary?.history?.length > 1 ? (
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={summary.history}>
                <defs>
                  <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E11D48" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#E11D48" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmtCurrency(v)} contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#FFFFFF' }} />
                <Area type="monotone" dataKey="net_worth" stroke="#E11D48" fill="url(#nwGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-600"><TrendingUp className="w-8 h-8 mx-auto mb-2" /><p>History will build over time</p></div>
          )}
        </div>
      </div>
      )}

      {/* Type Filter */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTypeFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-testid="asset-filter-all">
          All ({assets.length})
        </button>
        {uniqueTypes.map(t => {
          const info = ASSET_TYPES.find(at => at.value === t);
          const count = assets.filter(a => a.asset_type === t).length;
          return (
            <button key={t} onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === t ? 'text-gray-900' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              style={typeFilter === t ? { backgroundColor: TYPE_COLORS[t] } : {}}
              data-testid={`asset-filter-${t}`}>
              {info?.label || t} ({count})
            </button>
          );
        })}
      </div>

      {/* Assets Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="text-gray-500">Asset</TableHead>
              <TableHead className="text-gray-500">Type</TableHead>
              <TableHead className="text-gray-500">Institution</TableHead>
              <TableHead className="text-gray-500 text-right">Value</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow className="border-gray-200"><TableCell colSpan={5} className="text-center py-12 text-gray-600">
                <Wallet className="w-10 h-10 mx-auto mb-2" />
                <p className="font-medium">No assets yet</p>
                <p className="text-xs mt-1">Click "Add Asset" to start tracking</p>
              </TableCell></TableRow>
            ) : filtered.map(a => {
              const typeInfo = ASSET_TYPES.find(t => t.value === a.asset_type);
              const Icon = typeInfo?.icon || Wallet;
              return (
                <TableRow key={a.id} className="border-gray-200 hover:bg-gray-100/50" data-testid={`asset-row-${a.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${TYPE_COLORS[a.asset_type]}20` }}>
                        <Icon className="w-4 h-4" style={{ color: TYPE_COLORS[a.asset_type] }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.name}</p>
                        {a.account_number && <p className="text-xs text-gray-500">{a.account_number}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs px-2 py-0.5 rounded-full text-gray-700" style={{ backgroundColor: `${TYPE_COLORS[a.asset_type]}20` }}>{typeInfo?.label || a.asset_type}</span></TableCell>
                  <TableCell className="text-sm text-gray-500">{a.institution || '-'}</TableCell>
                  <TableCell className="text-right font-semibold text-blue-400">{fmtCurrencyFull(a.value)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditAsset(a)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(a)} className="text-red-500"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialogs */}
      <AssetDialog open={addOpen || !!editAsset} onOpenChange={v => { if (!v) { setAddOpen(false); setEditAsset(null); } }} asset={editAsset} companyId={currentCompany?.id} onSave={fetchData} defaultType={defaultAssetType} />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>Delete "{deleteTarget?.name}"? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
